/**
 * ============================================================================
 * 通用步骤加载器 Mixin (stepLoaderMixin)
 * ============================================================================
 * 
 * 【功能说明】
 * 这是一个可复用的 Vue Mixin，用于管理"多步骤加载"的 UI 交互。
 * 比如：重置鼠标、导入配置、固件更新等需要显示进度步骤的场景。
 * 
 * 【核心概念】
 * - 场景(Scene)：一个完整的步骤流程，比如"重置"是一个场景，"导入"是另一个场景
 * - 步骤(Step)：场景中的每一步，比如 "重置中" -> "重置成功" -> "初始化中" -> "完成"
 * - 同一时间只能有一个活动场景
 * 
 * 【使用步骤】
 * 1. 在组件中引入: mixins: [stepLoaderMixin]
 * 2. 实现 stepLoaderScenes() 方法，返回场景配置
 * 3. 调用 startStepLoader('场景名') 启动
 * 4. 在适当时机调用 setStepLoaderStep(步骤号) 推进步骤
 * 5. 调用 completeStepLoader('场景名') 完成流程
 * 
 * 【模板绑定】
 * <step-loader 
 *   :visible="stepLoaderVisible"      // 是否显示
 *   :steps="stepLoaderSteps"          // 步骤文案数组
 *   :currentStep="stepLoaderStep"     // 当前步骤索引
 * />
 * 
 * 【动态步骤文案】
 * 步骤可以是字符串或函数。如果是函数，会在渲染时调用获取实际文案：
 * steps: [
 *   '导出中',
 *   '选择路径',
 *   () => this.exportResult === 'cancelled' ? '导出取消' : '导出成功'
 * ]
 */

// ============================================================================
// 默认配置
// ============================================================================
const DEFAULT_CONFIG = {
  timeout: 30000,    // 默认超时时间 30 秒
  stepDelay: 800,    // 步骤切换间隔 800ms（让用户能看清每一步）
};

export default {
  // ==========================================================================
  // Data: Mixin 内部状态
  // ==========================================================================
  data() {
    return {
      // 使用 sl 前缀避免与组件变量冲突 (sl = stepLoader)
      slActiveScene: null,      // 当前活动场景名称
      slLoading: false,         // 是否正在加载
      slStep: 0,                // 当前步骤索引
      slTimeoutId: null,        // 超时定时器 ID
      slSimulateIds: [],        // 模拟定时器 ID 数组
    };
  },

  // ==========================================================================
  // Computed: 暴露给模板使用的计算属性
  // ==========================================================================
  computed: {
    /**
     * 是否显示加载器
     * 模板中使用: :visible="stepLoaderVisible"
     */
    stepLoaderVisible() {
      return this.slLoading;
    },

    /**
     * 当前步骤索引
     * 模板中使用: :currentStep="stepLoaderStep"
     */
    stepLoaderStep() {
      return this.slStep;
    },

    /**
     * 当前场景的步骤文案数组
     * 模板中使用: :steps="stepLoaderSteps"
     * 返回例如: ['重置中', '重置成功', '初始化中', '完成']
     * 
     * 支持动态步骤：如果步骤是函数，会调用函数获取实际文案
     */
    stepLoaderSteps() {
      if (!this.slActiveScene) return [];
      const config = this._getSceneConfig(this.slActiveScene);
      if (!config?.steps) return [];
      
      // 处理动态步骤：如果是函数则调用，否则直接返回字符串
      return config.steps.map(step => {
        if (typeof step === 'function') {
          return step.call(this);
        }
        return step;
      });
    },
  },

  // ==========================================================================
  // Methods: 方法
  // ==========================================================================
  methods: {
    // ------------------------------------------------------------------------
    // 【需要组件实现的方法】
    // ------------------------------------------------------------------------
    
    /**
     * 获取场景配置
     * 
     * 组件需要实现 stepLoaderScenes() 方法，返回场景配置对象。
     * 这个方法会去调用组件的 stepLoaderScenes() 获取配置。
     * 
     * @param {string} sceneName - 场景名称
     * @returns {Object|null} 场景配置
     */
    _getSceneConfig(sceneName) {
      // 检查组件是否实现了 stepLoaderScenes 方法
      if (typeof this.stepLoaderScenes !== 'function') {
        console.error('[StepLoader] 组件需要实现 stepLoaderScenes() 方法');
        return null;
      }
      const scenes = this.stepLoaderScenes();
      return scenes[sceneName];
    },

    // ------------------------------------------------------------------------
    // 【公开方法 - 组件调用这些方法来控制流程】
    // ------------------------------------------------------------------------

    /**
     * 启动步骤加载器
     * 
     * 调用示例: this.startStepLoader('reset')
     * 
     * 执行流程:
     * 1. 获取场景配置
     * 2. 清理之前可能存在的场景
     * 3. 初始化状态（loading=true, step=0）
     * 4. 执行场景的 onStart 回调（比如发送重置指令）
     * 5. 启动超时定时器
     * 6. 如果配置了模拟，启动模拟定时器（Qt模式用）
     * 
     * @param {string} sceneName - 场景名称
     * @param {Object} options - 可选的运行时配置
     */
    startStepLoader(sceneName, options = {}) {
      // 1. 获取场景配置
      const config = this._getSceneConfig(sceneName);
      if (!config) {
        console.error(`[StepLoader] 未找到场景配置: ${sceneName}`);
        return;
      }

      // 2. 清理之前的场景（如果有的话）
      this._cleanupStepLoader();

      // 3. 初始化状态
      this.slActiveScene = sceneName;
      this.slLoading = true;
      this.slStep = 0;

      // 4. 执行启动回调
      // 这里通常会发送指令，比如 this.$store.dispatch('loader/resetAM35')
      if (config.onStart) {
        config.onStart(options);
      }

      // 5. 设置超时保护
      // 如果超过指定时间还没完成，自动触发超时处理
      const timeout = options.timeout || config.timeout || DEFAULT_CONFIG.timeout;
      this.slTimeoutId = setTimeout(() => {
        this._handleTimeout();
      }, timeout);

      // 6. Qt模式模拟（因为Qt没有某些回调，需要用定时器模拟步骤推进）
      if (config.simulate?.enabled) {
        this._startSimulation(config.simulate);
      }
    },

    /**
     * 设置当前步骤（只能前进，不能后退）
     * 
     * 调用示例: this.setStepLoaderStep(2)
     * 
     * 为什么只能前进？
     * - 用户体验：步骤应该是线性推进的，后退会让用户困惑
     * - 防止错误：避免因为异步回调顺序问题导致步骤跳来跳去
     * 
     * @param {number} step - 目标步骤索引
     */
    setStepLoaderStep(step) {
      if (!this.slLoading) return;
      
      // 只有目标步骤大于当前步骤才更新
      if (step > this.slStep) {
        this.slStep = step;
      }
    },

    /**
     * 完成步骤加载器
     * 
     * 调用示例: this.completeStepLoader('reset')
     * 
     * 执行流程:
     * 1. 验证场景名称（可选）
     * 2. 清除超时定时器
     * 3. 确保所有步骤都显示过（快速补齐）
     * 4. 显示最后一步
     * 5. 延迟后关闭并执行 onComplete 回调
     * 
     * @param {string} sceneName - 场景名称（可选，用于验证）
     */
    completeStepLoader(sceneName) {
      // 验证：如果传了场景名，检查是否匹配当前活动场景
      if (sceneName && this.slActiveScene !== sceneName) return;
      if (!this.slLoading) return;

      const config = this._getSceneConfig(this.slActiveScene);
      if (!config) return;

      // 清除超时定时器（已经要完成了，不需要超时保护了）
      this._clearTimeout();

      // 确保步骤完整显示后再完成
      this._ensureStepsThenFinish(config);
    },

    /**
     * 强制关闭加载器（用于错误处理或取消操作）
     * 
     * 调用示例: this.closeStepLoader()
     */
    closeStepLoader() {
      this._cleanupStepLoader();
    },

    /**
     * 清理某个场景自己挂在实例上的兜底定时器
     *
     * @param {string} timerKey - 例如 "importCancelFallbackTimer"
     */
    clearSceneFallbackTimer(timerKey) {
      if (!timerKey || !this[timerKey]) return;
      clearTimeout(this[timerKey]);
      this[timerKey] = null;
    },

    /**
     * 判断 loadingFinish 的 payload 是否代表“文件选择被取消”
     *
     * 目前客户端在取消文件选择时会上报 null，某些链路里也可能出现空串/"null"/"undefined"。
     * 这些值都视为“没有有效结果”，应该走取消收尾，而不是继续停留在步骤中。
     *
     * @param {*} payload - 宿主透传回来的 loadingFinish.data
     * @returns {boolean}
     */
    isLoadingFinishCancelPayload(payload) {
      if (payload == null) {
        return true;
      }
      if (typeof payload !== "string") {
        return false;
      }
      const normalized = payload.trim().toLowerCase();
      return (
        normalized === "" ||
        normalized === "null" ||
        normalized === "undefined"
      );
    },

    /**
     * 为某个步骤场景安排“取消文件选择”的兜底收尾
     *
     * 为什么不立刻关闭？
     * - 某些客户端会先回 loadingFinish，再很快回 init 数据
     * - 这里给一个很短的缓冲窗口，让真正的成功回包优先到达
     *
     * @param {Object} options
     * @param {string} options.timerKey - 保存定时器的实例字段名
     * @param {string} options.sceneName - 需要确认仍处于该场景
     * @param {number} [options.delay=300] - 缓冲时间
     * @param {Function} [options.onCancel] - 真正取消时的收尾逻辑
     */
    scheduleSceneCancelFallback({
      timerKey,
      sceneName,
      delay = 300,
      onCancel,
    }) {
      if (!timerKey || !sceneName) return;

      this.clearSceneFallbackTimer(timerKey);
      this[timerKey] = setTimeout(() => {
        if (!(this.slLoading && this.slActiveScene === sceneName)) {
          this.clearSceneFallbackTimer(timerKey);
          return;
        }
        if (typeof onCancel === "function") {
          onCancel();
        }
      }, delay);
    },

    /**
     * 处理某个步骤场景里的 loadingFinish 取消/忽略逻辑
     *
     * 返回 true 代表该 payload 已经在这个场景里被消费，外层调用方应直接 return。
     *
     * @param {Object} options
     * @param {*} options.payload - loadingFinish.data
     * @param {string} options.sceneName - 例如 "import"
     * @param {string} options.timerKey - 例如 "importCancelFallbackTimer"
     * @param {Function} options.isSceneActive - 判断当前是否处于目标场景
     * @param {Function} options.onCancel - 取消确认后的收尾逻辑
     * @param {string} [options.cancelLog] - 取消时打印的日志
     * @param {string} [options.ignoreLog] - 非取消 payload 时打印的日志
     * @returns {boolean}
     */
    handleSceneLoadingFinish({
      payload,
      sceneName,
      timerKey,
      isSceneActive,
      onCancel,
      cancelLog = "",
      ignoreLog = "",
    }) {
      if (typeof isSceneActive !== "function" || !isSceneActive()) {
        return false;
      }

      if (this.isLoadingFinishCancelPayload(payload)) {
        if (cancelLog) {
          console.log(cancelLog);
        }
        this.scheduleSceneCancelFallback({
          timerKey,
          sceneName,
          onCancel,
        });
        return true;
      }

      if (ignoreLog) {
        console.log(ignoreLog);
      }
      return true;
    },

    /**
     * 组件销毁时调用，清理所有资源
     * 
     * 在组件的 destroyed() 或 beforeUnmount() 中调用:
     * destroyed() {
     *   this.cleanupStepLoaderOnDestroy();
     * }
     */
    cleanupStepLoaderOnDestroy() {
      this._cleanupStepLoader();
    },

    // ------------------------------------------------------------------------
    // 【私有方法 - 内部使用】
    // ------------------------------------------------------------------------

    /**
     * 确保所有步骤都显示过，然后完成
     * 
     * 为什么需要这个？
     * 有时候后端回调来得很快，比如步骤还在 0，但 initAM35Data 已经来了。
     * 这时候需要快速把 1、2 步骤都显示一下，让用户看到完整流程。
     * 
     * @param {Object} config - 场景配置
     */
    _ensureStepsThenFinish(config) {
      const maxStep = config.steps.length - 1;  // 最后一步的索引
      const delay = config.stepDelay || DEFAULT_CONFIG.stepDelay;

      // 如果已经在倒数第二步或更后面，直接完成
      if (this.slStep >= maxStep - 1) {
        this._finish(config);
      } else {
        // 需要补齐中间的步骤
        // 比如当前在 step 0，需要显示 1, 2，然后完成到 3
        const stepsToShow = [];
        for (let i = this.slStep + 1; i < maxStep; i++) {
          stepsToShow.push(i);
        }
        // 顺序显示这些步骤，每步间隔 delay 毫秒
        this._showStepsSequentially(stepsToShow, delay, () => {
          this._finish(config);
        });
      }
    },

    /**
     * 顺序显示步骤（递归实现）
     * 
     * @param {number[]} steps - 要显示的步骤索引数组
     * @param {number} delay - 每步间隔时间
     * @param {Function} callback - 全部显示完后的回调
     */
    _showStepsSequentially(steps, delay, callback) {
      // 递归终止条件：没有更多步骤了
      if (steps.length === 0) {
        callback();
        return;
      }

      // 取出第一个步骤，显示它
      const [first, ...rest] = steps;
      this.slStep = first;

      // 延迟后显示剩余步骤
      setTimeout(() => {
        this._showStepsSequentially(rest, delay, callback);
      }, delay);
    },

    /**
     * 完成流程
     * 
     * @param {Object} config - 场景配置
     */
    _finish(config) {
      const maxStep = config.steps.length - 1;
      const delay = config.stepDelay || DEFAULT_CONFIG.stepDelay;

      // 显示最后一步（比如"完成"）
      this.slStep = maxStep;

      // 延迟后关闭加载器并执行完成回调
      setTimeout(() => {
        this._cleanupStepLoader();

        // 执行完成回调（比如跳转到改键 tab）
        if (config.onComplete) {
          config.onComplete();
        }
      }, delay);
    },

    /**
     * 超时处理
     */
    _handleTimeout() {
      if (!this.slLoading) return;

      const config = this._getSceneConfig(this.slActiveScene);
      console.error(`[StepLoader] 场景 "${this.slActiveScene}" 超时`);

      // 清理资源
      this._cleanupStepLoader();

      // 执行超时回调（比如显示错误提示）
      if (config?.onTimeout) {
        config.onTimeout();
      }
    },

    /**
     * 启动模拟步骤（Qt模式使用）
     * 
     * 为什么需要模拟？
     * Qt模式下没有 resetAM35 的回调，无法知道重置成功的时机。
     * 所以用定时器模拟步骤推进，给用户一个视觉反馈。
     * 
     * @param {Object} simulate - 模拟配置 { enabled: true, delays: [2000, 1000] }
     */
    _startSimulation(simulate) {
      const delays = simulate.delays || [2000, 1000];
      let accumulatedDelay = 0;

      // 为每个步骤设置定时器
      // delays[0]=2000 表示 2 秒后显示步骤 1
      // delays[1]=1000 表示再过 1 秒（共 3 秒）后显示步骤 2
      delays.forEach((delay, index) => {
        accumulatedDelay += delay;
        const timerId = setTimeout(() => {
          // 只有当前步骤还没到这一步时才更新
          if (this.slLoading && this.slStep <= index) {
            this.slStep = index + 1;
          }
        }, accumulatedDelay);
        
        // 保存定时器 ID，以便清理
        this.slSimulateIds.push(timerId);
      });
    },

    /**
     * 清除超时定时器
     */
    _clearTimeout() {
      if (this.slTimeoutId) {
        clearTimeout(this.slTimeoutId);
        this.slTimeoutId = null;
      }
    },

    /**
     * 清理所有资源，重置状态
     */
    _cleanupStepLoader() {
      // 清除超时定时器
      this._clearTimeout();

      // 清除所有模拟定时器
      if (this.slSimulateIds && this.slSimulateIds.length) {
        this.slSimulateIds.forEach(id => clearTimeout(id));
      }
      this.slSimulateIds = [];

      // 重置状态
      this.slActiveScene = null;
      this.slLoading = false;
      this.slStep = 0;
    },
  },
};
